/**
 * R4-F1 — the Polymarket US dot of the persistent chrome (VenueCredStatus),
 * beside K and P: is this login's US account up right now?
 *
 * It starts no poll of its own. It is fed by the reads the page already makes:
 * - the shared account-presence read (#987, once per login, retried on the
 *   rail's cadence after a failure): whether this login has a US account it
 *   views as its own, and whether the first such account is enabled;
 * - one context read of that first own account per login (its readiness);
 * - the side rail's reads (#1034: every 10 s, none while hidden) whenever a
 *   rail is mounted (the board's Positions pop-up, the Tracker), which record
 *   their result here through `recordUsVenueRead` and replace the login read.
 *
 * States: green = an enabled, ready account; amber = an account present but
 * not ready (disabled key, service not running), or the rail's latest read
 * failed and it shows an earlier one; red = the read failed; gray = no US
 * account for this login (or not signed in / not read yet). The hover names
 * the reason and, from a rail read, when it was read. Every read is stamped
 * with the identity it was taken under; another login's never paints here.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { usReason } from '@/lib/polymarketUs/reason';
import type { UsReadiness } from '@/lib/polymarketUs/types';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { cachedUsFirstOwnAccount, useUsAccountPresenceState } from './useUsAccountPresence';

export type UsVenueTone = 'green' | 'amber' | 'red' | 'gray';
export type UsVenueDot = { tone: UsVenueTone; title: string };

/** What the rail records: the shape of `useUsRail`'s state that the dot reads
 * (structural, so this module never imports the view). */
export type UsVenueRailRead = {
  status: 'off' | 'loading' | 'ready' | 'unavailable';
  read: { accounts: readonly { accountId: string; readiness: UsReadiness | null; error: string | null; carriedFromMs?: number }[] } | null;
  error: string | null;
  readAtMs: number | null;
};

/** The identity a read is taken under: user, org and login epoch (as the rail stamps its state). */
export const usVenueIdentity = (): string => {
  const { user } = useAuthStore.getState();
  return `${user?.id ?? ''}:${user?.orgId ?? ''}:${getAuthSessionEpoch()}`;
};

let railRead: (UsVenueRailRead & { identity: string }) | null = null;
const listeners = new Set<() => void>();
const subscribeRail = (notify: () => void) => { listeners.add(notify); return () => { listeners.delete(notify); }; };
const railSnapshot = () => railRead;

/** The side rail's latest read, under the identity it was read for. */
export function recordUsVenueRead(identity: string, state: UsVenueRailRead): void {
  if (state.status === 'off') return;
  railRead = { identity, status: state.status, read: state.read, error: state.error, readAtMs: state.readAtMs };
  listeners.forEach((notify) => notify());
}

type ContextRead = { enabled: boolean; readiness: UsReadiness | null; error: string | null };
let contextCache: { key: string; read: Promise<ContextRead> } | null = null;

/** Test seam: forget the rail's read and the login's context read. */
export function resetUsVenueStatus(): void {
  railRead = null;
  contextCache = null;
}

const codeOf = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

/** One context read of the first own account per login; a failure is not kept,
 * so the next caller (a later mount) may read again. The rail refreshes it. */
function readFirstOwnContext(key: string, accountId: string, enabled: boolean): Promise<ContextRead> {
  if (contextCache?.key === key) return contextCache.read;
  const read = createUsBrowserAccountClient().selectAccount(accountId, 'own').then(
    (ctx) => ({ enabled: ctx.accountEnabled, readiness: ctx.readiness, error: null }),
    (error: unknown) => {
      if (contextCache?.key === key) contextCache = null;
      return { enabled, readiness: null, error: codeOf(error) };
    },
  );
  contextCache = { key, read };
  return read;
}

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const clock = (ms: number) => new Date(ms).toLocaleTimeString([], { hour12: false });

function readinessDot(readiness: UsReadiness, suffix = ''): UsVenueDot {
  if (readiness.state === 'ready') {
    return { tone: 'green', title: `Polymarket US ready${readiness.canSubmit ? '' : ' (view only: no submit permission on this account)'}${suffix}` };
  }
  return { tone: 'amber', title: `Polymarket US not ready — ${usReason(readiness.reason)}${suffix}` };
}

/** The dot from the rail's latest read for this identity; null when it has none or is still loading. */
function railDot(rail: (UsVenueRailRead & { identity: string }) | null, identity: string): UsVenueDot | null {
  if (!rail || rail.identity !== identity || rail.status === 'loading') return null;
  if (!rail.read) return { tone: 'red', title: `Polymarket US read failed — ${usReason(rail.error ?? 'US_READ_UNAVAILABLE')}` };
  const first = rail.read.accounts[0];
  if (!first) return { tone: 'gray', title: 'No Polymarket US account for this login' };
  const readAt = rail.readAtMs !== null ? clock(rail.readAtMs) : null;
  const at = readAt ? ` (read ${readAt})` : '';
  if (first.error && first.readiness === null) {
    return { tone: 'red', title: `Polymarket US account read failed — ${usReason(first.error)}${at}` };
  }
  // The rail kept an earlier read of this account (or of the whole rail): stale.
  const stale = first.error ? usReason(first.error) : rail.status === 'unavailable' ? usReason(rail.error ?? 'US_READ_UNAVAILABLE') : null;
  if (stale !== null) {
    const from = first.carriedFromMs !== undefined ? clock(first.carriedFromMs) : readAt;
    return { tone: 'amber', title: `Polymarket US: the latest read failed — ${stale}; showing ${from ? `the ${from} read` : 'an earlier read'}` };
  }
  return readinessDot(first.readiness ?? { state: 'unavailable', reason: 'US_EXECUTION_READINESS_NOT_PROVIDED' }, at);
}

export function useUsVenueStatus(): UsVenueDot {
  const authed = useAuthStore((s) => s.status === 'authenticated' && !!s.user?.orgId);
  const identity = useSyncExternalStore(subscribeAuth, usVenueIdentity);
  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);
  const presence = useUsAccountPresenceState(authed);
  const rail = useSyncExternalStore(subscribeRail, railSnapshot);
  const first = authed && presence.value === true ? cachedUsFirstOwnAccount(epoch) : undefined;
  const contextKey = first ? `${identity}|${first.accountId}` : null;
  const [context, setContext] = useState<(ContextRead & { key: string }) | null>(null);
  useEffect(() => {
    if (!contextKey || !first) return;
    let cancelled = false;
    void readFirstOwnContext(contextKey, first.accountId, first.accountEnabled).then((read) => {
      if (!cancelled) setContext({ key: contextKey, ...read });
    });
    return () => { cancelled = true; };
  }, [contextKey, first]);

  if (!authed) return { tone: 'gray', title: 'Polymarket US: not signed in' };
  if (presence.failed) return { tone: 'red', title: `Polymarket US read failed — ${usReason(presence.error ?? 'US_READ_UNAVAILABLE')}` };
  if (presence.value === false) return { tone: 'gray', title: 'No Polymarket US account for this login' };
  if (presence.value === null || !first) return { tone: 'gray', title: 'Polymarket US status resolving…' };
  const fromRail = railDot(rail, identity);
  if (fromRail) return fromRail;
  const own = context?.key === contextKey ? context : null;
  if (own?.error) return { tone: 'red', title: `Polymarket US account read failed — ${usReason(own.error)}` };
  if (own?.readiness) return readinessDot(own.readiness);
  if (!first.accountEnabled) return { tone: 'amber', title: `Polymarket US not ready — ${usReason('US_CREDENTIAL_DISABLED')}` };
  return { tone: 'gray', title: 'Polymarket US status resolving…' };
}
